import { reaction } from "mobx";
import { useCallback, useEffect, useRef, useState } from "react";
import { useResizeObserver } from "utils/useResizeObserver";
import { useCardLayout } from "../../Dashboard/Layout/CardLayoutContext";
import { useObservedHeight } from "../../Dashboard/Layout/useObservedHeight";
import ChartCardStore from "./ChartCardStore";

/**
 * Connects a chart card to the dashboard layout: redraws its chart when the
 * card changes size, holds the data grid still during a gesture, follows the
 * card's column count, and reports the chart's chrome height. Returns the refs
 * the card attaches to its elements.
 */
export function useChartCardLayout(store: ChartCardStore) {
    const cardLayout = useCardLayout();
    const layoutStore = cardLayout?.layoutStore;
    const chartContainerRef = useRef<HTMLDivElement | null>(null);
    const chartBodyRef = useRef<HTMLDivElement>(null);
    const [cardElement, setCardElement] = useState<HTMLDivElement | null>(null);
    const attachCard = useCallback((element: HTMLDivElement | null) => {
        chartContainerRef.current = element;
        setCardElement(element);
    }, []);

    /**
     * react-google-charts only redraws on a window resize, so a card that
     * changes size on its own has to redraw its chart. The same width drives
     * the chart's density decisions.
     */
    const redrawChart = useCallback(
        (width: number, height: number) => {
            store.widthStore.setMeasuredWidth(width);
            if (width <= 0 || height <= 0) return;
            store.chartWrapper?.draw();
        },
        [store],
    );

    /**
     * A redraw during a drag or resize is wasted work, so a size change that
     * arrives mid-gesture is remembered and the chart redraws once when the
     * gesture ends.
     */
    const redrawPendingRef = useRef(false);
    const redrawUnlessGesturing = useCallback(
        (width: number, height: number) => {
            if (layoutStore?.isGestureActive()) {
                redrawPendingRef.current = true;
                return;
            }
            redrawChart(width, height);
        },
        [layoutStore, redrawChart],
    );

    const { theRef: chartSizeRef } = useResizeObserver<HTMLDivElement>({
        box: "border",
        onResize: redrawUnlessGesturing,
        // One redraw when a window resize settles, not one per frame.
        debounceWait: 200,
    });

    /**
     * The data grid re-renders its rows and measures its scrollbars on every
     * width change, which costs more than a frame each time a resize crosses
     * a column. Its width is held at the start of a gesture, clipped while
     * the card is narrower, and released at the end, so it resizes once. Set
     * on the elements directly, because a render mid-gesture would make the
     * grid renderer drop the drag. The end of a gesture is also when a redraw
     * held back during it happens.
     */
    const dataGridFrameRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!layoutStore) return;
        const holdWidth = (hold: boolean) => {
            const frame = dataGridFrameRef.current;
            if (!frame?.parentElement) return;
            frame.style.width = hold ? `${frame.offsetWidth}px` : "";
            frame.parentElement.style.overflow = hold ? "hidden" : "";
        };
        const unsubscribeStart = layoutStore.subscribeGestureStart(() =>
            holdWidth(true),
        );
        const unsubscribeEnd = layoutStore.subscribeGestureEnd(() => {
            holdWidth(false);
            const plot = chartSizeRef.current;
            if (!redrawPendingRef.current || !plot) return;
            redrawPendingRef.current = false;
            const rect = plot.getBoundingClientRect();
            redrawChart(Math.round(rect.width), Math.round(rect.height));
        });
        return () => {
            unsubscribeStart();
            unsubscribeEnd();
        };
    }, [layoutStore, redrawChart, chartSizeRef]);

    // The card's column count decides the compact form on wide screens.
    useEffect(() => {
        if (!layoutStore) return;
        return reaction(
            () => layoutStore.getItemWidth(store.id),
            (columns) => store.widthStore.setLayoutColumns(columns),
            { fireImmediately: true },
        );
    }, [layoutStore, store]);

    /**
     * Reports everything in this card that is not the plot, which sets the
     * card's floor. Chrome is measured directly rather than through the plot,
     * because a plot squeezed to its minimum stops changing size while chrome
     * such as pagination or an expanded data grid can still grow. Until the
     * plot exists there is nothing to reserve room for, so nothing is reported.
     */
    useObservedHeight({
        root: cardElement,
        measure: (card) => {
            const body = chartBodyRef.current;
            const plot = chartSizeRef.current;
            if (!body || !plot) return null;

            const bodyStyle = window.getComputedStyle(body);
            const bodyPadding =
                parseFloat(bodyStyle.paddingTop || "0") +
                parseFloat(bodyStyle.paddingBottom || "0");
            // Unrounded: `offsetHeight` rounds up as well as down, and the
            // layout store drops the fraction itself.
            const aroundBody = Math.max(
                0,
                card.getBoundingClientRect().height -
                    body.getBoundingClientRect().height,
            );
            const besidePlot = [...body.children].reduce(
                (total, child) =>
                    child === plot
                        ? total
                        : total + child.getBoundingClientRect().height,
                0,
            );
            return aroundBody + bodyPadding + besidePlot;
        },
        onReport: layoutStore
            ? (height) => layoutStore.setMeasuredChromeHeight(store.id, height)
            : undefined,
        watch: (card) => {
            const body = chartBodyRef.current;
            return body ? [card, body, ...body.children] : [card];
        },
        // Expanding a data grid or showing pagination swaps the body's children.
        mutations: (card) => ({
            target: chartBodyRef.current ?? card,
            subtree: false,
        }),
    });

    return {
        chartContainerRef,
        attachCard,
        chartBodyRef,
        chartSizeRef,
        dataGridFrameRef,
    };
}
